// frontend/src/modules/marginCenter/pages/MarginCenterExplorerPage.tsx

import {
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  useNavigate,
  useSearchParams,
} from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  ButtonBase,
  Chip,
  Divider,
  InputAdornment,
  LinearProgress,
  MenuItem,
  Paper,
  Popover,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';

import SearchIcon from '@mui/icons-material/Search';

import {
  getMarginCenterCapabilities,
  getMarginCenterExplorer,
  getMarginCenterOverview,
} from '../services/marginCenterService';

import {
  getMarginCenterRevenueContractionIntelligence,
} from '../services/situationIntelligenceService';

import CustomerQuickContextDrawer
  from '../components/CustomerQuickContextDrawer';

import ProductQuickContextDrawer
  from '../components/ProductQuickContextDrawer';

import PageHeader, { useStickyOffset } from '../../pricingJourney/components/PageHeader';
import { withReturnTo } from '../../pricingJourney/navigation';
import CustomerRelationshipWorkspace
  from '../components/CustomerRelationshipWorkspace';

import MarginCenterTemporalContextBar, {
  buildRecommendedMarginCenterTemporalSearchParams,
  hasExplicitMarginCenterTemporalContext,
} from '../components/MarginCenterTemporalContextBar';

import type {
  MarginCenterComparisonMode,
  MarginCenterCustomerContextParams,
  MarginCenterProductContextParams,
  MarginCenterExplorerDimension,
  MarginCenterExplorerMetric,
  MarginCenterExplorerParams,
  MarginCenterExplorerRow,
  MarginCenterExplorerSignal,
  MarginCenterExplorerSortDirection,
  MarginCenterExplorerSortField,
  MarginCenterOverviewParams,
  MarginCenterTimeGrain,
} from '../types/marginCenter';


const DIMENSIONS: Array<{
  value: MarginCenterExplorerDimension;
  label: string;
}> = [
  { value: 'customer', label: 'Clientes' },
  { value: 'product', label: 'Productos' },
  { value: 'customer_product', label: 'Cliente · producto' },
];

const METRICS: Array<{
  value: MarginCenterExplorerMetric;
  label: string;
}> = [
  { value: 'revenue', label: 'Ingresos' },
  { value: 'margin_amount', label: 'Margen' },
  { value: 'margin_percentage', label: 'Margen %' },
  { value: 'cost_amount', label: 'Coste' },
  { value: 'units', label: 'Unidades' },
  { value: 'average_net_unit_price', label: 'Precio neto medio' },
  { value: 'average_unit_cost', label: 'Coste unitario medio' },
  { value: 'transaction_count', label: 'Transacciones' },
];

const SIGNALS: Array<{
  value: MarginCenterExplorerSignal;
  label: string;
  needsComparison: boolean;
}> = [
  {
    value: 'negative_margin',
    label: 'Margen negativo',
    needsComparison: false,
  },
  {
    value: 'margin_erosion',
    label: 'Menor margen %',
    needsComparison: true,
  },
  {
    value: 'revenue_contraction',
    label: 'Menos ingresos',
    needsComparison: true,
  },
  {
    value: 'inactive',
    label: 'Sin actividad',
    needsComparison: true,
  },
];

const SORT_FIELDS: Array<{
  value: MarginCenterExplorerSortField;
  label: string;
  needsComparison: boolean;
}> = [
  { value: 'entity_label', label: 'Entidad', needsComparison: false },
  { value: 'revenue', label: 'Ingresos', needsComparison: false },
  { value: 'margin_amount', label: 'Margen', needsComparison: false },
  { value: 'margin_percentage', label: 'Margen %', needsComparison: false },
  { value: 'cost_amount', label: 'Coste', needsComparison: false },
  { value: 'units', label: 'Unidades', needsComparison: false },
  { value: 'transaction_count', label: 'Transacciones', needsComparison: false },
  { value: 'average_net_unit_price', label: 'Precio neto medio', needsComparison: false },
  { value: 'average_unit_cost', label: 'Coste unitario medio', needsComparison: false },
  { value: 'cost_coverage_pct', label: 'Cobertura de coste', needsComparison: false },
  { value: 'resolved_revenue_coverage_pct', label: 'Cobertura de ingresos', needsComparison: false },
  { value: 'revenue_delta', label: 'Variación de ingresos', needsComparison: true },
  { value: 'revenue_delta_pct', label: 'Variación de ingresos %', needsComparison: true },
  { value: 'margin_amount_delta', label: 'Variación de margen', needsComparison: true },
  { value: 'margin_amount_delta_pct', label: 'Variación de margen %', needsComparison: true },
  { value: 'margin_percentage_delta_points', label: 'Variación de margen (p.p.)', needsComparison: true },
  { value: 'units_delta_pct', label: 'Variación de unidades %', needsComparison: true },
];

const COMPARISONS: Array<{
  value: MarginCenterComparisonMode;
  label: string;
}> = [
  { value: 'previous_period', label: 'Periodo anterior' },
  { value: 'year_over_year', label: 'Año anterior' },
  { value: 'custom', label: 'Personalizada' },
  { value: 'none', label: 'Sin comparación' },
];

const GRAINS: Array<{
  value: MarginCenterTimeGrain;
  label: string;
}> = [
  { value: 'day', label: 'Día' },
  { value: 'week', label: 'Semana' },
  { value: 'month', label: 'Mes' },
];

// O-12E: Explorer solo ofrece situaciones con destino analítico fiel.
const OVERVIEW_SITUATION_KEYS = [
  'customers-negative-margin',
  'customers-margin-erosion',
  'customers-revenue-contraction',
  'inactive-customers',
] as const;

type OverviewSituationKey =
  typeof OVERVIEW_SITUATION_KEYS[number];

function readOverviewSituationKey(
  value: string | null,
): OverviewSituationKey | null {
  if (
    value
    && OVERVIEW_SITUATION_KEYS.includes(
      value as OverviewSituationKey,
    )
  ) {
    return value as OverviewSituationKey;
  }

  return null;
}

function getOverviewSituationExplorerConfig(
  value: OverviewSituationKey,
): {
  signal: MarginCenterExplorerSignal;
  metric: MarginCenterExplorerMetric;
  sort: MarginCenterExplorerSortField;
  direction: MarginCenterExplorerSortDirection;
} {
  switch (value) {
    case 'customers-negative-margin':
      return {
        signal: 'negative_margin',
        metric: 'margin_amount',
        sort: 'margin_amount',
        direction: 'asc',
      };

    case 'customers-margin-erosion':
      return {
        signal: 'margin_erosion',
        metric: 'margin_percentage',
        sort: 'margin_percentage_delta_points',
        direction: 'asc',
      };

    case 'customers-revenue-contraction':
      return {
        signal: 'revenue_contraction',
        metric: 'revenue',
        sort: 'revenue_delta',
        direction: 'asc',
      };

    case 'inactive-customers':
      return {
        signal: 'inactive',
        metric: 'revenue',
        sort: 'revenue_delta',
        direction: 'asc',
      };
  }
}


interface ExplorerDraft {
  startDate: string;
  endDate: string;
  comparisonMode: MarginCenterComparisonMode;
  comparisonStartDate: string;
  comparisonEndDate: string;
  timeGrain: MarginCenterTimeGrain;
  dimension: MarginCenterExplorerDimension;
  metric: MarginCenterExplorerMetric;
  search: string;
  signal: '' | MarginCenterExplorerSignal;
  sortField: MarginCenterExplorerSortField;
  sortDirection: MarginCenterExplorerSortDirection;
  limit: string;
}


function readEnumParam<T extends string>(
  searchParams: URLSearchParams,
  key: string,
  allowed: readonly T[],
  fallback: T,
): T {
  const raw = searchParams.get(key);

  if (raw && allowed.includes(raw as T)) {
    return raw as T;
  }

  return fallback;
}


function readPositiveInt(
  raw: string | null,
  fallback: number,
  max: number,
): number {
  if (!raw) {
    return fallback;
  }

  const parsed = Number.parseInt(raw, 10);

  if (!Number.isFinite(parsed) || parsed < 1 || parsed > max) {
    return fallback;
  }

  return parsed;
}


function buildDraft(searchParams: URLSearchParams): ExplorerDraft {
  const metric = readEnumParam(
    searchParams,
    'metric',
    METRICS.map((item) => item.value),
    'revenue',
  );

  return {
    startDate: searchParams.get('from') ?? '',
    endDate: searchParams.get('to') ?? '',
    comparisonMode: readEnumParam(
      searchParams,
      'comparison',
      COMPARISONS.map((item) => item.value),
      'previous_period',
    ),
    comparisonStartDate: searchParams.get('comparisonFrom') ?? '',
    comparisonEndDate: searchParams.get('comparisonTo') ?? '',
    timeGrain: readEnumParam(
      searchParams,
      'grain',
      GRAINS.map((item) => item.value),
      'week',
    ),
    dimension: readEnumParam(
      searchParams,
      'dimension',
      DIMENSIONS.map((item) => item.value),
      'customer',
    ),
    metric,
    search: searchParams.get('search') ?? '',
    signal: readEnumParam(
      searchParams,
      'signal',
      SIGNALS.map((item) => item.value),
      '' as const,
    ),
    sortField: readEnumParam(
      searchParams,
      'sort',
      SORT_FIELDS.map((item) => item.value),
      metric,
    ),
    sortDirection: readEnumParam(
      searchParams,
      'direction',
      ['asc', 'desc'] as const,
      'desc',
    ),
    limit: String(readPositiveInt(searchParams.get('limit'), 50, 100)),
  };
}


function buildExplorerParams(
  searchParams: URLSearchParams,
): MarginCenterExplorerParams {
  const draft = buildDraft(searchParams);
  const cursor = searchParams.get('cursor')?.trim() || undefined;

  return {
    start_date: draft.startDate || undefined,
    end_date: draft.endDate || undefined,
    comparison_mode: draft.comparisonMode,
    comparison_start_date:
      draft.comparisonMode === 'custom'
        ? draft.comparisonStartDate || undefined
        : undefined,
    comparison_end_date:
      draft.comparisonMode === 'custom'
        ? draft.comparisonEndDate || undefined
        : undefined,
    time_grain: draft.timeGrain,
    dimension: draft.dimension,
    metric: draft.metric,
    search: draft.search.trim() || undefined,
    signal: draft.signal || undefined,
    sort_field: draft.sortField,
    sort_direction: draft.sortDirection,
    limit: readPositiveInt(draft.limit, 50, 100),
    cursor,
  };
}


const numberFormatter = new Intl.NumberFormat('es-ES', {
  maximumFractionDigits: 2,
});

const integerFormatter = new Intl.NumberFormat('es-ES', {
  maximumFractionDigits: 0,
});


function formatNumber(
  value: number | null | undefined,
): string {
  if (
    value === null
    || value === undefined
  ) {
    return '—';
  }

  return numberFormatter.format(value);
}

function formatCurrency(
  value: number | null | undefined,
  currencyCode: string | null,
): string {
  if (
    value === null
    || value === undefined
  ) {
    return '—';
  }

  if (!currencyCode) {
    return formatNumber(value);
  }

  try {
    return new Intl.NumberFormat(
      'es-ES',
      {
        style: 'currency',
        currency: currencyCode,
        maximumFractionDigits: 2,
      },
    ).format(value);
  } catch {
    return formatNumber(value);
  }
}

function formatInteger(value: number | null | undefined): string {
  if (value === null || value === undefined) return '—';
  return integerFormatter.format(value);
}

function formatPct(value: number | null | undefined): string {
  if (value === null || value === undefined) return '—';
  return `${numberFormatter.format(value)} %`;
}

function formatSignedPct(
  value: number | null | undefined,
): string {
  if (
    value === null
    || value === undefined
  ) {
    return '—';
  }

  return `${
    value > 0
      ? '+'
      : ''
  }${numberFormatter.format(value)} %`;
}

function formatSignedPoints(
  value: number | null | undefined,
): string {
  if (
    value === null
    || value === undefined
  ) {
    return '—';
  }

  return `${
    value > 0
      ? '+'
      : ''
  }${numberFormatter.format(value)} pp`;
}

function getMetricLabel(
  metric: MarginCenterExplorerMetric,
): string {
  return (
    METRICS.find(
      (item) =>
        item.value === metric,
    )?.label
    ?? metric
  );
}

function getMetricValue(
  row: MarginCenterExplorerRow,
  metric: MarginCenterExplorerMetric,
  period: 'current' | 'comparison',
): number | null {
  const metrics =
    period === 'current'
      ? row.current
      : row.comparison;

  if (!metrics) {
    return null;
  }

  return metrics[metric];
}

function formatMetricValue(
  metric: MarginCenterExplorerMetric,
  value: number | null | undefined,
  currencyCode: string | null,
): string {
  if (
    metric === 'revenue'
    || metric === 'margin_amount'
    || metric === 'cost_amount'
    || metric === 'average_net_unit_price'
    || metric === 'average_unit_cost'
  ) {
    return formatCurrency(
      value,
      currencyCode,
    );
  }

  if (metric === 'margin_percentage') {
    return formatPct(value);
  }

  if (metric === 'transaction_count') {
    return formatInteger(value);
  }

  return formatNumber(value);
}

function getMetricChangeDefinition(
  metric: MarginCenterExplorerMetric,
): {
  sortField: MarginCenterExplorerSortField;
  label: string;
  format: 'percentage' | 'points';
} | null {
  switch (metric) {
    case 'revenue':
      return {
        sortField: 'revenue_delta_pct',
        label: 'Δ ingresos %',
        format: 'percentage',
      };

    case 'margin_amount':
      return {
        sortField: 'margin_amount_delta_pct',
        label: 'Δ margen %',
        format: 'percentage',
      };

    case 'margin_percentage':
      return {
        sortField:
          'margin_percentage_delta_points',
        label: 'Δ margen % (pp)',
        format: 'points',
      };

    case 'units':
      return {
        sortField: 'units_delta_pct',
        label: 'Δ unidades %',
        format: 'percentage',
      };

    default:
      return null;
  }
}

function getMetricChangeValue(
  row: MarginCenterExplorerRow,
  metric: MarginCenterExplorerMetric,
): number | null {
  if (!row.changes) {
    return null;
  }

  switch (metric) {
    case 'revenue':
      return row.changes.revenue_delta_pct;

    case 'margin_amount':
      return row.changes.margin_amount_delta_pct;

    case 'margin_percentage':
      return row.changes.margin_percentage_delta_points;

    case 'units':
      return row.changes.units_delta_pct;

    default:
      return null;
  }
}

type ExplorerContextMetric =
  | 'revenue'
  | 'margin_percentage'
  | 'units';


function getContextMetrics(
  metric: MarginCenterExplorerMetric,
): ExplorerContextMetric[] {
  switch (metric) {
    case 'revenue':
      return [
        'margin_percentage',
        'units',
      ];

    case 'margin_amount':
      return [
        'revenue',
        'margin_percentage',
      ];

    case 'margin_percentage':
      return [
        'revenue',
        'units',
      ];

    case 'units':
      return [
        'revenue',
        'margin_percentage',
      ];

    case 'transaction_count':
      return [
        'revenue',
        'units',
      ];

    case 'average_net_unit_price':
      return [
        'units',
        'margin_percentage',
      ];

    case 'cost_amount':
      return [
        'revenue',
        'margin_percentage',
      ];

    case 'average_unit_cost':
      return [
        'units',
        'margin_percentage',
      ];

    default:
      return [
        'revenue',
        'margin_percentage',
      ];
  }
}

function formatMetricChange(
  value: number | null,
  format: 'percentage' | 'points',
): string {
  if (format === 'points') {
    return formatSignedPoints(value);
  }

  return formatSignedPct(value);
}

function getDirectionalColor(
  value: number | null,
): string {
  if (value === null || value === 0) {
    return '#94a3b8';
  }

  return value > 0
    ? '#34d399'
    : '#fb7185';
}

function ContextMetricCell({
  row,
  metric,
  currencyCode,
  hasComparison,
}: {
  row: MarginCenterExplorerRow;
  metric: ExplorerContextMetric;
  currencyCode: string | null;
  hasComparison: boolean;
}) {
  const changeDefinition =
    getMetricChangeDefinition(metric);

  const changeValue =
    getMetricChangeValue(
      row,
      metric,
    );

  return (
    <TableCell
      align="right"
      sx={{
        whiteSpace: 'nowrap',
      }}
    >
      <Stack
        direction="row"
        spacing={0.9}
        alignItems="baseline"
        justifyContent="flex-end"
      >
        <Typography
          variant="body2"
          component="span"
          sx={{
            color: '#cbd5e1',
            fontWeight: 650,
          }}
        >
          {formatMetricValue(
            metric,
            getMetricValue(
              row,
              metric,
              'current',
            ),
            currencyCode,
          )}
        </Typography>

        {hasComparison
          && changeDefinition
          && (
          <Typography
            variant="caption"
            component="span"
            sx={{
              fontWeight: 750,
              color:
                getDirectionalColor(
                  changeValue,
                ),
            }}
          >
            {formatMetricChange(
              changeValue,
              changeDefinition.format,
            )}
          </Typography>
        )}
      </Stack>
    </TableCell>
  );
}

function getSignalLabel(signal: MarginCenterExplorerSignal): string {
  return SIGNALS.find((item) => item.value === signal)?.label ?? signal;
}

function ExplorerEntityCell({
  row,
  onOpenCustomer,
  onOpenProduct,
  onOpenRelationship,
}: {
  row: MarginCenterExplorerRow;

  onOpenCustomer: (
    customerId: string,
  ) => void;

  onOpenProduct: (
    productId: string,
  ) => void;

  onOpenRelationship: (
    customerId: string,
    productId: string,
  ) => void;
}) {
  const isCustomer =
    row.entity.dimension === 'customer'
    && Boolean(
      row.entity.customer_id,
    );

  const isProduct =
    row.entity.dimension === 'product'
    && Boolean(
      row.entity.product_id,
    );


  const isRelationship =
    row.entity.dimension === 'customer_product'
    && Boolean(
      row.entity.customer_id,
    )
    && Boolean(
      row.entity.product_id,
    );

  const isNavigable =
    isCustomer
    || isProduct
    || isRelationship;

  const content = (
    <Box sx={{ textAlign: 'left' }}>
      <Typography
        variant="body2"
        sx={{
          color: isNavigable
            ? '#7dd3fc'
            : '#f8fafc',
          fontWeight: 800,
          lineHeight: 1.35,
          overflowWrap: 'anywhere',
        }}
      >
        {row.entity.label}
      </Typography>

      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: 'block', mt: 0.15 }}
      >
        {row.entity.dimension === 'customer'
          ? row.entity.customer_id
          : row.entity.dimension === 'product'
            ? row.entity.product_id
            : (
              `${row.entity.customer_id ?? '—'} · `
              + `${row.entity.product_id ?? '—'}`
            )}
      </Typography>
    </Box>
  );

  return (
    <TableCell
      sx={{
        width: 320,
        minWidth: 320,
        maxWidth: 320,
      }}
    >
      {isNavigable ? (
        <ButtonBase
          onClick={() => {
            if (
              isCustomer
              && row.entity.customer_id
            ) {
              onOpenCustomer(
                row.entity.customer_id,
              );

              return;
            }

            if (
              isProduct
              && row.entity.product_id
            ) {
              onOpenProduct(
                row.entity.product_id,
              );

              return;
            }

            if (
              isRelationship
              && row.entity.customer_id
              && row.entity.product_id
            ) {
              onOpenRelationship(
                row.entity.customer_id,
                row.entity.product_id,
              );
            }
          }}
          sx={{
            width: '100%',
            justifyContent: 'flex-start',
            borderRadius: 1.5,
            px: 0.5,
            py: 0.35,
            mx: -0.5,
            my: -0.35,

            '&:hover': {
              bgcolor:
                'rgba(56,189,248,0.08)',
            },
          }}
        >
          {content}
        </ButtonBase>
      ) : (
        content
      )}
    </TableCell>
  );
}


function SortableHeader({
  field,
  label,
  activeField,
  direction,
  align = 'right',
  highlight = false,
  onSort,
}: {
  field: MarginCenterExplorerSortField;
  label: string;
  activeField: MarginCenterExplorerSortField;
  direction: MarginCenterExplorerSortDirection;
  align?: 'left' | 'right';
  highlight?: boolean;
  onSort: (
    field: MarginCenterExplorerSortField,
  ) => void;
}) {
  const isActive = activeField === field;

  return (
    <TableCell
      align={align}
      sortDirection={isActive ? direction : false}
      sx={{
        whiteSpace: 'nowrap',
        width:
          field === 'entity_label'
            ? 320
            : undefined,

        minWidth:
          field === 'entity_label'
            ? 320
            : highlight
              ? 150
              : (
                field === 'revenue'
                || field === 'margin_percentage'
                || field === 'units'
              )
                ? 180
                : 120,

        maxWidth:
          field === 'entity_label'
            ? 320
            : undefined,
        fontWeight: 800,
        bgcolor:
          highlight
            ? '#13283a'
            : '#0f172a',
        borderBottom:
          '1px solid rgba(148,163,184,0.18)',
      }}
    >
      <TableSortLabel
        active={isActive}
        direction={isActive ? direction : 'asc'}
        hideSortIcon={false}
        onClick={() => onSort(field)}
        sx={{
          color: '#cbd5e1',
          fontWeight: 800,
          '&:hover': {
            color: '#f8fafc',
          },
          '&.Mui-active': {
            color: '#38bdf8',
          },
          '&.Mui-active:hover': {
            color: '#7dd3fc',
          },
          '& .MuiTableSortLabel-icon': {
            color: isActive ? '#38bdf8' : '#64748b',
            opacity: isActive ? 1 : 0.48,
          },
        }}
      >
        {label}
      </TableSortLabel>
    </TableCell>
  );
}


function LoadingRows({
  columnCount,
}: {
  columnCount: number;
}) {
  return (
    <>
      {Array.from({ length: 8 }).map((_, rowIndex) => (
        <TableRow key={rowIndex}>
          {Array.from({ length: columnCount }).map((__, cellIndex) => (
            <TableCell key={cellIndex}>
              <Skeleton
                variant="text"
                width={cellIndex === 0 ? '75%' : '60%'}
              />
            </TableCell>
          ))}
        </TableRow>
      ))}
    </>
  );
}

export default function MarginCenterExplorerPage() {
  const navigate = useNavigate();
  // PRC-027: las fichas abiertas desde aquí vuelven al Explorer.
  const withExplorerOrigin = (target: string) => withReturnTo(
    target,
    `/margin-center/explorer${searchParams.toString() ? `?${searchParams.toString()}` : ''}`,
  );
  const [searchParams, setSearchParams] = useSearchParams();
  // PRC-032: la cabecera de la tabla se queda justo bajo la barra de filtros.
  const [filtersRef, filtersOffset] = useStickyOffset<HTMLDivElement>();

  const originSituation = readOverviewSituationKey(
    searchParams.get('origin') === 'overview-situation'
      ? searchParams.get('originSituation')
      : null,
  );

  const [searchInput, setSearchInput] = useState(
    () => searchParams.get('search') ?? '',
  );
  const [cursorHistory, setCursorHistory] = useState<string[]>([]);
  const [situationAnchorEl, setSituationAnchorEl] =
    useState<HTMLElement | null>(null);

  const appliedParams = useMemo(
    () => buildExplorerParams(searchParams),
    [searchParams],
  );

  const selectedCustomerId =
    searchParams.get('customer')?.trim()
    || null;

  const selectedProductId =
    searchParams.get('product')?.trim()
    || null;

  const selectedRelationshipCustomerId =
    searchParams
      .get('relationshipCustomer')
      ?.trim()
    || null;

  const selectedRelationshipProductId =
    searchParams
      .get('relationshipProduct')
      ?.trim()
    || null;

  const customerContextParams =
    useMemo<MarginCenterCustomerContextParams>(
      () => ({
        start_date: appliedParams.start_date,
        end_date: appliedParams.end_date,
        comparison_mode: appliedParams.comparison_mode,
        comparison_start_date:
          appliedParams.comparison_start_date,
        comparison_end_date:
          appliedParams.comparison_end_date,
        time_grain: appliedParams.time_grain,
      }),
      [appliedParams],
    );


  const productContextParams =
    useMemo<MarginCenterProductContextParams>(
      () => ({
        start_date: appliedParams.start_date,
        end_date: appliedParams.end_date,
        comparison_mode: appliedParams.comparison_mode,
        comparison_start_date:
          appliedParams.comparison_start_date,
        comparison_end_date:
          appliedParams.comparison_end_date,
        time_grain: appliedParams.time_grain,
      }),
      [appliedParams],
    );

  const capabilitiesQuery = useQuery({
    queryKey: ['margin-center', 'capabilities'],
    queryFn: getMarginCenterCapabilities,
  });

  const hasExplicitTemporalContext =
    hasExplicitMarginCenterTemporalContext(
      searchParams,
    );

  const recommendedTemporalSearchParams =
    useMemo(
      () => (
        buildRecommendedMarginCenterTemporalSearchParams(
          searchParams,
          capabilitiesQuery.data
            ?.dataset
            .last_transaction_date,
        )
      ),
      [
        capabilitiesQuery.data
          ?.dataset
          .last_transaction_date,
        searchParams,
      ],
    );

  const temporalContextReady =
    hasExplicitTemporalContext
    || (
      capabilitiesQuery.isFetched
      && recommendedTemporalSearchParams === null
    );

  useEffect(() => {
    if (!recommendedTemporalSearchParams) {
      return;
    }

    setSearchParams(
      recommendedTemporalSearchParams,
      { replace: true },
    );
  }, [
    recommendedTemporalSearchParams,
    setSearchParams,
  ]);

  const originOverviewParams =
    useMemo<MarginCenterOverviewParams>(
      () => ({
        start_date: appliedParams.start_date,
        end_date: appliedParams.end_date,
        comparison_mode: appliedParams.comparison_mode,
        comparison_start_date:
          appliedParams.comparison_start_date,
        comparison_end_date:
          appliedParams.comparison_end_date,
        time_grain: appliedParams.time_grain,
      }),
      [appliedParams],
    );

  // O-12C: contexto de Situación recalculado con el contrato Overview.
  const originOverviewQuery = useQuery({
    queryKey: [
      'margin-center',
      'overview',
      'explorer-origin-situation',
      originOverviewParams,
    ],
    queryFn: () => getMarginCenterOverview(originOverviewParams),
    enabled:
      Boolean(originSituation)
      && temporalContextReady,
    staleTime: 20_000,
  });

  // CD-INT-02A: materialidad exhaustiva para la situación de contracción.
  const revenueContractionIntelligenceQuery = useQuery({
    queryKey: [
      'margin-center',
      'situation-intelligence',
      'revenue-contraction',
      originOverviewParams,
    ],
    queryFn: () =>
      getMarginCenterRevenueContractionIntelligence({
        ...originOverviewParams,
        top_n: 5,
      }),
    enabled: (
      temporalContextReady
      && originSituation === 'customers-revenue-contraction'
      && appliedParams.dimension === 'customer'
      && appliedParams.signal === 'revenue_contraction'
    ),
    staleTime: 20_000,
  });

  const explorerQuery = useQuery({
    queryKey: ['margin-center', 'explorer', appliedParams],
    queryFn: () => getMarginCenterExplorer(appliedParams),
    enabled: temporalContextReady,
    placeholderData: (previousData) => previousData,
  });

  const explorer = explorerQuery.data;
  const selection = explorerQuery.isPlaceholderData
    ? undefined
    : explorer?.selection;

  const currencyCode =
    capabilitiesQuery.data
      ?.presentation
      .currency_code
    ?? null;

  const originSituationSummary = useMemo(() => {
    const overview = originOverviewQuery.data;

    if (
      !originSituation
      || !overview
      || overview.status !== 'ok'
    ) {
      return null;
    }

    const expectedSignal =
      getOverviewSituationExplorerConfig(originSituation).signal;

    if (
      appliedParams.dimension !== 'customer'
      || appliedParams.signal !== expectedSignal
    ) {
      return null;
    }

    const activeCustomers =
      overview.current.active_customers;

    const customerShare = (
      count: number,
      base: number,
      baseLabel: string,
    ) => ({
      value: `${formatInteger(count)} clientes`,
      detail: base > 0
        ? `${
          formatPct((count / base) * 100)
        } de ${
          formatInteger(base)
        } ${baseLabel}`
        : 'Sin base comparativa calculable',
    });

    switch (originSituation) {
      case 'customers-negative-margin': {
        const count =
          overview.signals.customers_with_negative_margin;

        if (typeof count !== 'number' || count <= 0) {
          return null;
        }

        return {
          label: 'Clientes con margen negativo',
          ...customerShare(
            count,
            activeCustomers,
            'clientes activos',
          ),
          note:
            'La tabla está filtrada por margen agregado negativo en el periodo.',
        };
      }

      case 'customers-margin-erosion': {
        const count =
          overview.signals.customers_with_margin_erosion;

        if (typeof count !== 'number' || count <= 0) {
          return null;
        }

        return {
          label: 'Clientes con menor margen %',
          ...customerShare(
            count,
            activeCustomers,
            'clientes activos',
          ),
          note:
            'El margen % actual es inferior al comparado; la señal no atribuye una causa.',
        };
      }

      case 'customers-revenue-contraction': {
        const count =
          overview.signals.customers_with_revenue_contraction;

        if (typeof count !== 'number' || count <= 0) {
          return null;
        }

        return {
          label: 'Clientes con menos ingresos',
          ...customerShare(
            count,
            activeCustomers,
            'clientes activos',
          ),
          note:
            'Tienen ingresos en ambos periodos, pero facturan menos en el actual.',
        };
      }

      case 'inactive-customers': {
        const count =
          overview.signals.inactive_customers;
        const comparisonActive =
          overview.comparison?.active_customers ?? 0;

        if (typeof count !== 'number' || count <= 0) {
          return null;
        }

        return {
          label: 'Clientes sin actividad',
          ...customerShare(
            count,
            comparisonActive,
            'clientes activos en comparación',
          ),
          note:
            'Tenían ingresos en el periodo comparado y registran cero en el actual.',
        };
      }
    }
  }, [
    appliedParams.dimension,
    appliedParams.signal,
    currencyCode,
    originOverviewQuery.data,
    originSituation,
  ]);

  const availableOverviewSituations = useMemo(() => {
    const overview = originOverviewQuery.data;

    if (!overview || overview.status !== 'ok') {
      return [];
    }

    const situations: Array<{
      key: OverviewSituationKey;
      label: string;
      value: string;
    }> = [];

    const negativeMargin =
      overview.signals.customers_with_negative_margin;

    if (
      typeof negativeMargin === 'number'
      && negativeMargin > 0
    ) {
      situations.push({
        key: 'customers-negative-margin',
        label: 'Clientes con margen negativo',
        value: `${formatInteger(negativeMargin)} clientes`,
      });
    }

    const marginErosion =
      overview.signals.customers_with_margin_erosion;

    if (
      typeof marginErosion === 'number'
      && marginErosion > 0
    ) {
      situations.push({
        key: 'customers-margin-erosion',
        label: 'Clientes con menor margen %',
        value: `${formatInteger(marginErosion)} clientes`,
      });
    }

    const revenueContraction =
      overview.signals.customers_with_revenue_contraction;

    if (
      typeof revenueContraction === 'number'
      && revenueContraction > 0
    ) {
      situations.push({
        key: 'customers-revenue-contraction',
        label: 'Clientes con menos ingresos',
        value: `${formatInteger(revenueContraction)} clientes`,
      });
    }

    const inactive =
      overview.signals.inactive_customers;

    if (
      typeof inactive === 'number'
      && inactive > 0
    ) {
      situations.push({
        key: 'inactive-customers',
        label: 'Clientes sin actividad',
        value: `${formatInteger(inactive)} clientes`,
      });
    }

    return situations;
  }, [originOverviewQuery.data]);

  useEffect(() => {
    const urlSearch = searchParams.get('search') ?? '';

    setSearchInput((current) =>
      current === urlSearch
        ? current
        : urlSearch
    );
  }, [searchParams]);

  useEffect(() => {
    const normalized = searchInput.trim();
    const current = searchParams.get('search') ?? '';

    if (normalized === current) return;

    const timer = window.setTimeout(() => {
      const next = new URLSearchParams(searchParams);

      if (normalized) next.set('search', normalized);
      else next.delete('search');

      next.delete('cursor');
      setCursorHistory([]);
      setSearchParams(next, { replace: true });
    }, 350);

    return () => window.clearTimeout(timer);
  }, [searchInput, searchParams, setSearchParams]);

  useEffect(() => {
    if (
      !explorer
      || explorer.status === 'empty_dataset'
      || explorerQuery.isPlaceholderData
    ) {
      return;
    }

    const next = new URLSearchParams(searchParams);
    const appliedSelection = explorer.selection;
    let changed = false;

    const setValue = (
      key: string,
      value: string | number | null,
    ) => {
      if (value === null || value === '') {
        if (next.has(key)) {
          next.delete(key);
          changed = true;
        }
        return;
      }

      const normalized = String(value);
      if (next.get(key) !== normalized) {
        next.set(key, normalized);
        changed = true;
      }
    };

    setValue('from', appliedSelection.start_date);
    setValue('to', appliedSelection.end_date);
    setValue('comparison', appliedSelection.comparison_mode);
    setValue('comparisonFrom', appliedSelection.comparison_start_date);
    setValue('comparisonTo', appliedSelection.comparison_end_date);
    setValue('grain', appliedSelection.time_grain);
    setValue('dimension', appliedSelection.dimension);
    setValue('metric', appliedSelection.metric);
    setValue('search', appliedSelection.search);
    setValue('signal', appliedSelection.signal);
    setValue('sort', appliedSelection.sort_field);
    setValue('direction', appliedSelection.sort_direction);
    setValue('limit', appliedSelection.limit);

    if (changed) setSearchParams(next, { replace: true });
  }, [
    explorer,
    explorerQuery.isPlaceholderData,
    searchParams,
    setSearchParams,
  ]);

  const capabilityByDimension = useMemo(() => {
    const map = new Map<string, string>();

    capabilitiesQuery.data?.dimensions.forEach((dimension) => {
      map.set(dimension.key, dimension.status);
    });

    return map;
  }, [capabilitiesQuery.data]);

  const appliedDimension: MarginCenterExplorerDimension =
    selection?.dimension ?? appliedParams.dimension ?? 'customer';

  const appliedMetric: MarginCenterExplorerMetric =
    selection?.metric ?? appliedParams.metric ?? 'revenue';

  const appliedSortField: MarginCenterExplorerSortField =
    selection?.sort_field ?? appliedParams.sort_field ?? appliedMetric;

  const appliedSortDirection: MarginCenterExplorerSortDirection =
    selection?.sort_direction ?? appliedParams.sort_direction ?? 'desc';

  const appliedSignal =
    selection?.signal ?? appliedParams.signal ?? null;

  const setImmediateSelection = (
    changes: Record<string, string | null>,
  ) => {
    const next = new URLSearchParams(searchParams);

    Object.entries(changes).forEach(([key, value]) => {
      if (value === null || value === '') next.delete(key);
      else next.set(key, value);
    });

    next.delete('cursor');
    setCursorHistory([]);
    setSearchParams(next);
  };

  const handleDimensionChange = (
    _: React.MouseEvent<HTMLElement>,
    nextDimension: MarginCenterExplorerDimension | null,
  ) => {
    if (!nextDimension || nextDimension === appliedDimension) return;

    setImmediateSelection({
      dimension: nextDimension,
      signal:
        nextDimension === 'customer'
          ? searchParams.get('signal')
          : null,
    });
  };

  const handleMetricChange = (
    nextMetric: MarginCenterExplorerMetric,
  ) => {
    if (nextMetric === appliedMetric) {
      return;
    }

    setImmediateSelection({
      metric: nextMetric,
      sort: nextMetric,
      direction: 'desc',
    });
  };


  const handleSort = (
    field: MarginCenterExplorerSortField,
  ) => {
    const nextDirection: MarginCenterExplorerSortDirection =
      appliedSortField === field
        ? appliedSortDirection === 'asc'
          ? 'desc'
          : 'asc'
        : field === 'entity_label'
          ? 'asc'
          : 'desc';

    setImmediateSelection({
      sort: field,
      direction: nextDirection,
    });
  };

  const clearSignal = () => {
    setImmediateSelection({ signal: null });
  };

  const applyOverviewSituation = (
    nextSituation: OverviewSituationKey,
  ) => {
    const config =
      getOverviewSituationExplorerConfig(nextSituation);
    const next = new URLSearchParams(searchParams);

    next.set('origin', 'overview-situation');
    next.set('originSituation', nextSituation);
    next.set('dimension', 'customer');
    next.set('metric', config.metric);
    next.set('signal', config.signal);
    next.set('sort', config.sort);
    next.set('direction', config.direction);
    next.set('limit', '50');

    next.delete('cursor');
    next.delete('search');
    next.delete('customer');
    next.delete('product');
    next.delete('relationshipCustomer');
    next.delete('relationshipProduct');

    setSearchInput('');
    setCursorHistory([]);
    setSituationAnchorEl(null);
    setSearchParams(next);
  };

  const openCustomerContext = (
    customerId: string,
  ) => {
    const next =
      new URLSearchParams(searchParams);

    next.delete('product');

    next.delete(
      'relationshipCustomer',
    );

    next.delete(
      'relationshipProduct',
    );

    next.set(
      'customer',
      customerId,
    );

    setSearchParams(next);
  };

  const openProductContext = (
    productId: string,
  ) => {
    const next =
      new URLSearchParams(searchParams);

    next.delete('customer');

    next.delete(
      'relationshipCustomer',
    );

    next.delete(
      'relationshipProduct',
    );

    next.set(
      'product',
      productId,
    );

    setSearchParams(next);
  };


  const openRelationshipContext = (
    customerId: string,
    productId: string,
  ) => {
    const next =
      new URLSearchParams(searchParams);

    next.delete('customer');
    next.delete('product');

    next.set(
      'relationshipCustomer',
      customerId,
    );

    next.set(
      'relationshipProduct',
      productId,
    );

    setSearchParams(next);
  };

  const closeRelationshipContext = () => {
    const next =
      new URLSearchParams(searchParams);

    next.delete(
      'relationshipCustomer',
    );

    next.delete(
      'relationshipProduct',
    );

    setSearchParams(
      next,
      {
        replace: true,
      },
    );
  };

  const closeCustomerContext = () => {
    const next = new URLSearchParams(searchParams);
    next.delete('customer');

    setSearchParams(next, { replace: true });
  };

  const closeProductContext = () => {
    const next =
      new URLSearchParams(searchParams);

    next.delete('product');

    setSearchParams(
      next,
      {
        replace: true,
      },
    );
  };


  const openFullCustomerContext = () => {
    if (!selectedCustomerId) return;

    const query = searchParams.toString();

    navigate(withExplorerOrigin(
      `/margin-center/customers/${
        encodeURIComponent(selectedCustomerId)
      }${query ? `?${query}` : ''}`,
    ));
  };

  const openFullProductContext = () => {
    if (!selectedProductId) return;

    const next =
      new URLSearchParams(searchParams);

    next.delete('product');
    next.delete('customer');

    next.delete(
      'relationshipCustomer',
    );

    next.delete(
      'relationshipProduct',
    );

    const query =
      next.toString();

    navigate(withExplorerOrigin(
      `/margin-center/products/${
        encodeURIComponent(
          selectedProductId,
        )
      }${query ? `?${query}` : ''}`,
    ));
  };

  const prepareExplorerTemporalSearchParams = (
    next: URLSearchParams,
  ) => {
    const nextComparison =
      next.get('comparison')
      ?? 'previous_period';

    if (nextComparison === 'none') {
      const nextSignal =
        next.get('signal');

      const signalNeedsComparison =
        nextSignal
        && SIGNALS.find(
          (item) =>
            item.value === nextSignal,
        )?.needsComparison;

      if (signalNeedsComparison) {
        next.delete('signal');
      }

      const nextSortField =
        (
          next.get('sort')
          ?? appliedSortField
        ) as MarginCenterExplorerSortField;

      const sortNeedsComparison =
        SORT_FIELDS.find(
          (item) =>
            item.value === nextSortField,
        )?.needsComparison;

      if (sortNeedsComparison) {
        next.set(
          'sort',
          appliedMetric,
        );

        next.set(
          'direction',
          'desc',
        );
      }
    }

    setCursorHistory([]);
  };

  const goToNextPage = () => {
    const nextCursor = explorer?.page.next_cursor;
    if (!nextCursor) return;

    setCursorHistory((current) => [
      ...current,
      searchParams.get('cursor') ?? '',
    ]);

    const next = new URLSearchParams(searchParams);
    next.set('cursor', nextCursor);
    setSearchParams(next);
  };

  const goToPreviousPage = () => {
    if (cursorHistory.length === 0) return;

    const previousCursor =
      cursorHistory[cursorHistory.length - 1];

    setCursorHistory((current) => current.slice(0, -1));

    const next = new URLSearchParams(searchParams);
    if (previousCursor) next.set('cursor', previousCursor);
    else next.delete('cursor');

    setSearchParams(next);
  };

  const activeMetricLabel =
    getMetricLabel(
      appliedMetric,
    );

  const comparisonMode =
    selection?.comparison_mode
    ?? appliedParams.comparison_mode
    ?? 'previous_period';

  const hasComparison =
    comparisonMode !== 'none';

  const metricChangeDefinition =
    getMetricChangeDefinition(
      appliedMetric,
    );

  const showMetricChange =
    hasComparison
    && metricChangeDefinition !== null;

  const contextMetrics =
    getContextMetrics(
      appliedMetric,
    );

  const costSensitiveMetric =
    appliedMetric === 'cost_amount'
    || appliedMetric === 'margin_amount'
    || appliedMetric === 'margin_percentage'
    || appliedMetric === 'average_unit_cost';

  const hasPartialCostCoverage =
    explorer?.status === 'ok'
    && explorer.rows.some(
      (row) =>
        row.coverage
          .current
          .cost_coverage_pct
        < 100,
    );

  const showCostCoverage =
    costSensitiveMetric
    && hasPartialCostCoverage;

  const visibleColumnCount =
    2
    + (
      hasComparison
        ? 1
        : 0
    )
    + (
      showMetricChange
        ? 1
        : 0
    )
    + contextMetrics.length
    + (
      showCostCoverage
        ? 1
        : 0
    );


  return (
    <Box
      sx={{
        width: '100%',
        boxSizing: 'border-box',
        px: { xs: 2, md: 3, lg: 4 },
        py: 3,
      }}
    >
      <Stack spacing={1.5}>
        {/* PRC-032: cabecera común; «Volver a Overview» si se abrió desde Overview (PRC-027). */}
        <PageHeader
          eyebrow="Centro de decisión"
          title="Explorer"
          description="Localiza clientes, productos y relaciones, compáralos y profundiza sin perder el contexto."
          back="origin"
          sx={{ mb: 0 }}
        />
        <Paper
          ref={filtersRef}
          elevation={0}
          sx={{
            // PRC-032: filtros globales fijos al desplazarse.
            display: explorer && explorer.status !== 'empty_dataset' ? undefined : 'none',
            position: 'sticky',
            top: 8,
            zIndex: 5,
            borderRadius: 3,
            border:
              '1px solid rgba(148,163,184,0.16)',
            bgcolor:
              'rgba(15,23,42,0.58)',
            backdropFilter:
              'blur(10px)',
            px: {
              xs: 2,
              md: 2.5,
            },
            py: {
              xs: 1,
              md: 1,
            },
          }}
        >
          <Stack spacing={1.25}>
            {explorer
              && explorer.status !== 'empty_dataset'
              && (
              <Stack
                direction={{ xs: 'column', lg: 'row' }}
                spacing={1}
                alignItems={{ xs: 'stretch', lg: 'center' }}
                justifyContent="space-between"
              >
                <Box
                  sx={{
                    maxWidth: '100%',
                    flexShrink: 0,
                  }}
                >
                  <MarginCenterTemporalContextBar
                    selection={explorer.selection}
                    dataset={explorer.dataset}
                    compact
                    normalizeSearchParams={
                      prepareExplorerTemporalSearchParams
                    }
                  />
                </Box>

                {(originSituationSummary
                  || availableOverviewSituations.length > 0) && (
                  <>
                    {/* O-12D: Situación compacta en la fila temporal y cambio sin volver al Overview. */}
                    <Paper
                      elevation={0}
                      sx={{
                        minHeight: 52,
                        maxWidth: { xs: '100%', lg: 560 },
                        flex: { lg: '1 1 auto' },
                        px: 1.25,
                        py: 0.65,
                        borderRadius: 2,
                        border:
                          '1px solid rgba(56,189,248,0.18)',
                        bgcolor: 'rgba(8,47,73,0.13)',
                        display: 'flex',
                        alignItems: 'center',
                      }}
                    >
                      <Stack
                        direction="row"
                        spacing={1.25}
                        alignItems="center"
                        justifyContent="space-between"
                        sx={{ width: '100%', minWidth: 0 }}
                      >
                        <Box sx={{ minWidth: 0 }}>
                          <Typography
                            variant="caption"
                            sx={{
                              display: 'block',
                              color: '#38bdf8',
                              fontWeight: 850,
                              lineHeight: 1.15,
                            }}
                          >
                            Situación observada
                          </Typography>

                          {originSituationSummary ? (
                            <Box sx={{ mt: 0.2, minWidth: 0 }}>
                              <Stack
                                direction="row"
                                spacing={0.75}
                                alignItems="baseline"
                                sx={{ minWidth: 0 }}
                              >
                                <Typography
                                  variant="body2"
                                  noWrap
                                  sx={{
                                    color: '#f8fafc',
                                    fontWeight: 800,
                                    minWidth: 0,
                                  }}
                                >
                                  {originSituationSummary.label}
                                </Typography>

                                <Typography
                                  variant="body2"
                                  sx={{
                                    color: '#cbd5e1',
                                    fontWeight: 900,
                                    flexShrink: 0,
                                  }}
                                >
                                  · {originSituationSummary.value}
                                </Typography>
                              </Stack>

                              {(
                                originSituation
                                  === 'customers-revenue-contraction'
                                && revenueContractionIntelligenceQuery
                                  .data
                                  ?.status === 'ok'
                                && revenueContractionIntelligenceQuery
                                  .data
                                  .population
                                && revenueContractionIntelligenceQuery
                                  .data
                                  .materiality
                                && revenueContractionIntelligenceQuery
                                  .data
                                  .concentration
                              ) && (
                                <Typography
                                  variant="caption"
                                  noWrap
                                  sx={{
                                    display: 'block',
                                    color: '#94a3b8',
                                    mt: 0.15,
                                    lineHeight: 1.2,
                                  }}
                                >
                                  {
                                    formatInteger(
                                      revenueContractionIntelligenceQuery
                                        .data
                                        .population
                                        .comparable_customer_count,
                                    )
                                  } comparables
                                  {' · '}
                                  {
                                    formatCurrency(
                                      revenueContractionIntelligenceQuery
                                        .data
                                        .materiality
                                        .revenue_contraction_amount,
                                      currencyCode,
                                    )
                                  } de contracción
                                  {' · Top 5 '}
                                  {
                                    formatPct(
                                      revenueContractionIntelligenceQuery
                                        .data
                                        .concentration
                                        .top_n_share_of_contraction_pct,
                                    )
                                  }
                                </Typography>
                              )}
                            </Box>
                          ) : (
                            <Typography
                              variant="body2"
                              sx={{
                                color: '#94a3b8',
                                fontWeight: 750,
                                mt: 0.2,
                              }}
                            >
                              Selecciona una situación para investigar
                            </Typography>
                          )}
                        </Box>

                        <Button
                          size="small"
                          variant="outlined"
                          onClick={(event) =>
                            setSituationAnchorEl(event.currentTarget)
                          }
                          disabled={availableOverviewSituations.length === 0}
                          sx={{
                            textTransform: 'none',
                            fontWeight: 800,
                            flexShrink: 0,
                            whiteSpace: 'nowrap',
                          }}
                        >
                          Ver situaciones
                        </Button>
                      </Stack>
                    </Paper>

                    <Popover
                      open={Boolean(situationAnchorEl)}
                      anchorEl={situationAnchorEl}
                      onClose={() => setSituationAnchorEl(null)}
                      anchorOrigin={{
                        vertical: 'bottom',
                        horizontal: 'right',
                      }}
                      transformOrigin={{
                        vertical: 'top',
                        horizontal: 'right',
                      }}
                      slotProps={{
                        paper: {
                          sx: {
                            mt: 0.75,
                            width: 390,
                            maxWidth: 'calc(100vw - 32px)',
                            borderRadius: 2.25,
                            border:
                              '1px solid rgba(148,163,184,0.18)',
                            bgcolor: '#0f172a',
                            backgroundImage: 'none',
                            boxShadow:
                              '0 18px 50px rgba(2,6,23,0.34)',
                          },
                        },
                      }}
                    >
                      <Box sx={{ p: 1.25 }}>
                        <Typography
                          variant="subtitle2"
                          sx={{
                            color: '#f8fafc',
                            fontWeight: 850,
                            px: 0.25,
                            pb: 0.8,
                          }}
                        >
                          Situaciones observadas
                        </Typography>

                        <Stack spacing={0.55}>
                          {availableOverviewSituations.map((situation) => (
                            <ButtonBase
                              key={situation.key}
                              onClick={() =>
                                applyOverviewSituation(situation.key)
                              }
                              sx={{
                                width: '100%',
                                px: 1,
                                py: 0.85,
                                borderRadius: 1.5,
                                textAlign: 'left',
                                border:
                                  situation.key === originSituation
                                    ? '1px solid rgba(56,189,248,0.30)'
                                    : '1px solid rgba(148,163,184,0.10)',
                                bgcolor:
                                  situation.key === originSituation
                                    ? 'rgba(8,47,73,0.20)'
                                    : 'rgba(2,6,23,0.20)',
                                '&:hover': {
                                  bgcolor: 'rgba(8,47,73,0.20)',
                                },
                              }}
                            >
                              <Stack
                                direction="row"
                                spacing={1.25}
                                justifyContent="space-between"
                                alignItems="center"
                                sx={{ width: '100%' }}
                              >
                                <Typography
                                  variant="body2"
                                  sx={{
                                    color: '#e2e8f0',
                                    fontWeight: 800,
                                    minWidth: 0,
                                  }}
                                >
                                  {situation.label}
                                </Typography>

                                <Typography
                                  variant="body2"
                                  sx={{
                                    color: '#f8fafc',
                                    fontWeight: 900,
                                    flexShrink: 0,
                                  }}
                                >
                                  {situation.value}
                                </Typography>
                              </Stack>
                            </ButtonBase>
                          ))}
                        </Stack>
                      </Box>
                    </Popover>
                  </>
                )}
              </Stack>
            )}
          </Stack>
        </Paper>

        {capabilitiesQuery.isError && (
          <Alert severity="warning">
            No se pudo cargar la configuración de la cuenta.
            El Explorer mantiene disponible el contrato analítico actual.
          </Alert>
        )}

        <Paper variant="outlined" sx={{ overflow: { xs: 'hidden', lg: 'visible' } }}>
          <Box
            sx={{
              px: 2,
              py: 1.5,
              display: 'flex',
              flexDirection: { xs: 'column', lg: 'row' },
              alignItems: { xs: 'stretch', lg: 'center' },
              gap: 1.5,
              borderBottom: 1,
              borderColor: 'divider',
            }}
          >
            <ToggleButtonGroup
              exclusive
              size="small"
              value={appliedDimension}
              onChange={handleDimensionChange}
              aria-label="Dimensión del Explorer"
              sx={{
                flexShrink: 0,
                '& .MuiToggleButton-root': {
                  px: 1.75,
                  textTransform: 'none',
                  whiteSpace: 'nowrap',
                  color: '#94a3b8',
                  fontWeight: 750,
                  borderColor: 'rgba(148,163,184,0.2)',
                  '&:hover': {
                    color: '#e2e8f0',
                    bgcolor: 'rgba(255,255,255,0.03)',
                  },
                  '&.Mui-selected': {
                    color: '#f8fafc',
                    bgcolor: 'rgba(56,189,248,0.12)',
                    borderColor: 'rgba(56,189,248,0.28)',
                  },
                  '&.Mui-selected:hover': {
                    bgcolor: 'rgba(56,189,248,0.16)',
                  },
                },
              }}
            >
              {DIMENSIONS.map((item) => (
                <ToggleButton
                  key={item.value}
                  value={item.value}
                  disabled={
                    capabilityByDimension.get(item.value) === 'unavailable'
                  }
                >
                  {item.label}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>

            <TextField
              select
              size="small"
              label="Métrica principal"
              value={appliedMetric}
              onChange={(event) => {
                handleMetricChange(
                  event.target
                    .value as MarginCenterExplorerMetric,
                );
              }}
              sx={{
                minWidth: {
                  xs: '100%',
                  sm: 210,
                },

                '& .MuiOutlinedInput-root': {
                  bgcolor:
                    'rgba(2,6,23,0.28)',
                  color: '#f8fafc',

                  '& fieldset': {
                    borderColor:
                      'rgba(148,163,184,0.2)',
                  },

                  '&:hover fieldset': {
                    borderColor:
                      'rgba(148,163,184,0.34)',
                  },

                  '&.Mui-focused fieldset': {
                    borderColor:
                      'rgba(56,189,248,0.55)',
                  },
                },

                '& .MuiInputLabel-root': {
                  color: '#94a3b8',
                },
              }}
            >
              {METRICS.map(
                (item) => (
                  <MenuItem
                    key={item.value}
                    value={item.value}
                  >
                    {item.label}
                  </MenuItem>
                ),
              )}
            </TextField>

            <TextField
              size="small"
              value={searchInput}
              onChange={(event) => setSearchInput(event.target.value)}
              placeholder={
                appliedDimension === 'customer'
                  ? 'Buscar cliente o ID'
                  : appliedDimension === 'product'
                    ? 'Buscar producto o ID'
                    : 'Buscar cliente, producto o ID'
              }
              sx={{
                minWidth: { xs: '100%', lg: 300 },
                flexGrow: 1,
                maxWidth: { lg: 520 },
                '& .MuiOutlinedInput-root': {
                  bgcolor: 'rgba(2,6,23,0.28)',
                  color: '#f8fafc',
                  '& fieldset': {
                    borderColor: 'rgba(148,163,184,0.2)',
                  },
                  '&:hover fieldset': {
                    borderColor: 'rgba(148,163,184,0.34)',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: 'rgba(56,189,248,0.55)',
                  },
                },
                '& .MuiSvgIcon-root': {
                  color: '#94a3b8',
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          {appliedSignal && (
            <Box
              sx={{
                px: 2,
                py: 1,
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                borderBottom: 1,
                borderColor: 'divider',
              }}
            >
              <Typography variant="caption" color="text.secondary">
                Filtro activo
              </Typography>
              <Chip
                size="small"
                label={`Señal: ${getSignalLabel(appliedSignal)}`}
                onDelete={clearSignal}
              />
            </Box>
          )}

          {explorerQuery.isFetching && !explorerQuery.isLoading && (
            <LinearProgress />
          )}

          {explorerQuery.isError && !explorer && (
            <Box sx={{ p: 3 }}>
              <Alert
                severity="error"
                action={
                  <Button
                    color="inherit"
                    size="small"
                    onClick={() => explorerQuery.refetch()}
                  >
                    Reintentar
                  </Button>
                }
              >
                No se pudo consultar el Explorer.
              </Alert>
            </Box>
          )}

          {explorer?.status === 'empty_dataset' && (
            <Box sx={{ px: 3, py: 8, textAlign: 'center' }}>
              <Typography variant="h6">
                Esta cuenta todavía no tiene transacciones
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mt: 0.75 }}
              >
                El Explorer estará disponible cuando exista actividad
                comercial que analizar.
              </Typography>
            </Box>
          )}

          {explorer?.status === 'no_results' && (
            <Box sx={{ px: 3, py: 8, textAlign: 'center' }}>
              <Typography variant="h6">
                No hay entidades para esta selección
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mt: 0.75 }}
              >
                Cambia la búsqueda, la dimensión o el periodo para ampliar
                el resultado.
              </Typography>
            </Box>
          )}

          {(explorerQuery.isLoading || explorer?.status === 'ok') && (
            <>

              {/* PRC-032: una sola barra vertical (la de la página); en pantallas
                  anchas la cabecera de la tabla se queda fija bajo los filtros. */}
              <TableContainer
                sx={{
                  minHeight: 360,
                  overflowX: { xs: 'auto', lg: 'visible' },
                  overflowY: { xs: 'hidden', lg: 'visible' },
                }}
              >
                <Table
                  stickyHeader
                  size="small"
                  sx={{
                    minWidth: { xs: 1040, lg: 0 },
                    '& .MuiTableCell-stickyHeader': { top: { lg: filtersOffset } },
                    '& .MuiTableCell-root': {
                      py: 1.3,
                      px: 1.75,
                      borderColor: 'rgba(148,163,184,0.12)',
                      color: '#cbd5e1',
                    },
                  }}
                >
                  <TableHead>
                    <TableRow>
                      <SortableHeader
                        field="entity_label"
                        label="Entidad"
                        activeField={appliedSortField}
                        direction={appliedSortDirection}
                        align="left"
                        onSort={handleSort}
                      />

                      <SortableHeader
                        field={appliedMetric}
                        label={activeMetricLabel}
                        activeField={appliedSortField}
                        direction={appliedSortDirection}
                        highlight
                        onSort={handleSort}
                      />

                      {hasComparison && (
                        <TableCell
                          align="right"
                          sx={{
                            whiteSpace: 'nowrap',
                            minWidth: 140,
                            fontWeight: 800,
                            bgcolor: '#0f172a',
                            color: '#94a3b8',
                            borderBottom:
                              '1px solid rgba(148,163,184,0.18)',
                          }}
                        >
                          {activeMetricLabel} comp.
                        </TableCell>
                      )}

                      {showMetricChange
                        && metricChangeDefinition
                        && (
                        <SortableHeader
                          field={
                            metricChangeDefinition
                              .sortField
                          }
                          label={
                            metricChangeDefinition
                              .label
                          }
                          activeField={
                            appliedSortField
                          }
                          direction={
                            appliedSortDirection
                          }
                          onSort={handleSort}
                        />
                      )}

                      {contextMetrics.map(
                        (metric) => (
                          <SortableHeader
                            key={metric}
                            field={metric}
                            label={
                              getMetricLabel(
                                metric,
                              )
                            }
                            activeField={
                              appliedSortField
                            }
                            direction={
                              appliedSortDirection
                            }
                            onSort={
                              handleSort
                            }
                          />
                        ),
                      )}

                      {showCostCoverage && (
                        <SortableHeader
                          field="cost_coverage_pct"
                          label="Cobertura coste"
                          activeField={
                            appliedSortField
                          }
                          direction={
                            appliedSortDirection
                          }
                          onSort={handleSort}
                        />
                      )}
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {explorerQuery.isLoading ? (
                      <LoadingRows
                        columnCount={
                          visibleColumnCount
                        }
                      />
                    ) : (
                      explorer?.rows.map((row) => (
                        <TableRow
                          key={row.entity.key}
                          hover
                          sx={{
                            transition: 'background-color 0.16s ease',
                            '&:hover': {
                              bgcolor: 'rgba(56,189,248,0.045)',
                            },
                          }}
                        >
                          <ExplorerEntityCell
                            row={row}
                            onOpenCustomer={
                              openCustomerContext
                            }
                            onOpenProduct={
                              openProductContext
                            }
                            onOpenRelationship={
                              openRelationshipContext
                            }
                          />

                          <TableCell
                            align="right"
                            sx={{
                              bgcolor:
                                'rgba(14,165,233,0.035)',
                              color: '#f8fafc !important',
                              fontWeight: 800,
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {formatMetricValue(
                              appliedMetric,
                              getMetricValue(
                                row,
                                appliedMetric,
                                'current',
                              ),
                              currencyCode,
                            )}
                          </TableCell>

                          {hasComparison && (
                            <TableCell
                              align="right"
                              sx={{
                                color:
                                  '#94a3b8 !important',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {formatMetricValue(
                                appliedMetric,
                                getMetricValue(
                                  row,
                                  appliedMetric,
                                  'comparison',
                                ),
                                currencyCode,
                              )}
                            </TableCell>
                          )}

                          {showMetricChange
                            && metricChangeDefinition
                            && (
                            <TableCell
                              align="right"
                              sx={{
                                color:
                                  `${
                                    getDirectionalColor(
                                      getMetricChangeValue(
                                        row,
                                        appliedMetric,
                                      ),
                                    )
                                  } !important`,
                                fontWeight: 800,
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {formatMetricChange(
                                getMetricChangeValue(
                                  row,
                                  appliedMetric,
                                ),
                                metricChangeDefinition
                                  .format,
                              )}
                            </TableCell>
                          )}

                          {contextMetrics.map(
                            (metric) => (
                              <ContextMetricCell
                                key={metric}
                                row={row}
                                metric={metric}
                                currencyCode={
                                  currencyCode
                                }
                                hasComparison={
                                  hasComparison
                                }
                              />
                            ),
                          )}

                          {showCostCoverage && (
                            <TableCell
                              align="right"
                            >
                              {formatPct(
                                row.coverage
                                  .current
                                  .cost_coverage_pct,
                              )}
                            </TableCell>
                          )}
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              <Divider />

              <Box
                sx={{
                  px: 2,
                  py: 1.5,
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  gap: 1,
                  alignItems: { xs: 'stretch', sm: 'center' },
                  justifyContent: 'space-between',
                }}
              >
                <Typography
                  variant="caption"
                  color="text.secondary"
                >
                  {explorer?.status === 'ok'
                    ? `Mostrando ${
                      explorer.page.returned_count
                    } entidades en esta página.`
                    : 'Cargando entidades…'}
                </Typography>

                <Stack direction="row" spacing={1}>
                  <Button
                    variant="outlined"
                    size="small"
                    disabled={
                      cursorHistory.length === 0
                      || explorerQuery.isFetching
                    }
                    onClick={goToPreviousPage}
                  >
                    Anterior
                  </Button>

                  <Button
                    variant="contained"
                    size="small"
                    disabled={
                      !explorer?.page.has_more
                      || !explorer?.page.next_cursor
                      || explorerQuery.isFetching
                    }
                    onClick={goToNextPage}
                  >
                    Siguiente
                  </Button>
                </Stack>
              </Box>
            </>
          )}
        </Paper>
      </Stack>

      <CustomerQuickContextDrawer
        customerId={selectedCustomerId}
        params={customerContextParams}
        currencyCode={currencyCode}
        onClose={closeCustomerContext}
        onOpenFull={openFullCustomerContext}
      />

      <ProductQuickContextDrawer
        productId={selectedProductId}
        contextParams={productContextParams}
        currencyCode={currencyCode}
        onClose={closeProductContext}
        onOpenFull={openFullProductContext}
      />

      {selectedRelationshipCustomerId
        && selectedRelationshipProductId
        && (
        <CustomerRelationshipWorkspace
          customerId={
            selectedRelationshipCustomerId
          }
          productId={
            selectedRelationshipProductId
          }
          startDate={
            selection?.start_date
            ?? appliedParams.start_date
            ?? null
          }
          endDate={
            selection?.end_date
            ?? appliedParams.end_date
            ?? null
          }
          comparisonMode={
            selection?.comparison_mode
            ?? appliedParams.comparison_mode
            ?? 'previous_period'
          }
          comparisonStartDate={
            selection
              ?.comparison_start_date
            ?? appliedParams
              .comparison_start_date
            ?? null
          }
          comparisonEndDate={
            selection
              ?.comparison_end_date
            ?? appliedParams
              .comparison_end_date
            ?? null
          }
          timeGrain={
            selection?.time_grain
            ?? appliedParams.time_grain
            ?? 'week'
          }
          onClose={
            closeRelationshipContext
          }
        />
      )}
    </Box>
  );
}
